// في ملف context/CompanyWalletContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../api/client';
import { PaginatedResponse, WalletTransaction } from '../types/stores';
import { useAuth } from './AuthContext';

interface WalletStats {
  totalRevenue: number;
  pendingBalance: number;
  totalWithdrawals: number;
  lastWithdrawal?: string;
}

interface CompanyWalletContextType {
  walletBalance: number;
  recentTransactions: WalletTransaction[];
  walletStats: WalletStats | null;
  isLoading: boolean;
  isLoadingTransactions: boolean;
  
  // إضافة خصائص التبويق
  currentPage: number;
  totalPages: number;
  totalTransactions: number;
  transactionsPerPage: number;
  
  addTransaction: (transaction: Omit<WalletTransaction, 'id' | 'created_at' | 'completed_at'>) => Promise<void>;
  withdrawFunds: (amount: number, method: string) => Promise<boolean>;
  fetchTransactions: (page?: number, startDate?: Date, endDate?: Date) => Promise<PaginatedResponse<WalletTransaction>>;
  setPage: (page: number) => void;
}

const CompanyWalletContext = createContext<CompanyWalletContextType | undefined>(undefined);

export const CompanyWalletProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [recentTransactions, setRecentTransactions] = useState<WalletTransaction[]>([]);
  const [walletStats, setWalletStats] = useState<WalletStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
    const { user,  } = useAuth();
  
  // إضافة states للتبويق
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [transactionsPerPage] = useState(10); // يمكن تعديل العدد حسب الحاجة

  const fetchWalletData = async () => {
    setIsLoading(true);
    try {
      if (user){

        const balanceRes = await api.get<{ balance: number; stats: WalletStats }>('/wallet/');
        setWalletBalance(balanceRes.data.balance);
        setWalletStats(balanceRes.data.stats);
  
        await fetchTransactions(1);
      }
    } catch (error) {
      console.error('Failed to fetch wallet data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTransactions = async (
    page: number = 1,
    startDate?: Date,
    endDate?: Date
  ): Promise<PaginatedResponse<WalletTransaction>> => {
    setIsLoadingTransactions(true);
    try {
      const params: any = {
        page,
        limit: transactionsPerPage
      };
      
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();

      const res = await api.get<PaginatedResponse<WalletTransaction>>('/transactions/', { params });
      
      setRecentTransactions(res.data.results);
      setCurrentPage(page);
      setTotalPages(Math.ceil(res.data.count / transactionsPerPage));
      setTotalTransactions(res.data.count);
      
      return res.data;
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      setRecentTransactions([]);
      return { results: [], count: 0, next: null, previous: null };
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  const setPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      fetchTransactions(page);
    }
  };

  // باقي الدوال تبقى كما هي...
  const addTransaction = async (transaction: Omit<WalletTransaction, 'id' | 'created_at' | 'completed_at'>) => {
    try {
      const res = await api.post<WalletTransaction>('/transactions/', transaction);
      
      // إضافة المعاملة الجديدة في الصفحة الأولى
      setRecentTransactions(prev => {
        if (currentPage === 1) {
          return [res.data, ...prev.slice(0, transactionsPerPage - 1)];
        }
        return prev;
      });

      if (res.data.type === 'collect' || res.data.type === 'send') {
        if (res.data.status === 'completed') {
          setWalletBalance(prev =>
            res.data.type === 'collect' ? prev + parseFloat(res.data.net_amount) : prev - parseFloat(res.data.net_amount)
          );
        }
      }

      await fetchWalletData();
    } catch (error) {
      console.error('Failed to add transaction:', error);
      throw error;
    }
  };

  const withdrawFunds = async (amount: number, method: string): Promise<boolean> => {
    if (amount > walletBalance) throw new Error('Insufficient funds');

    try {
      const res = await api.post<WalletTransaction>('/wallets/withdraw/', { amount, method });
      
      await addTransaction({
        amount: res.data.amount,
        fee: res.data.fee,
        net_amount: res.data.net_amount,
        type: 'withdraw',
        status: res.data.status,
        sender_wallet: res.data.sender_wallet,
        receiver_wallet: res.data.receiver_wallet,
        reference_id: res.data.reference_id,
        invoice_data: res.data.invoice_data,
        metadata: res.data.metadata,
        note: res.data.note,
      });
      
      return true;
    } catch (error) {
      console.error('Withdrawal failed:', error);
      return false;
    }
  };

  useEffect(() => {
    fetchWalletData();
  }, []);

  return (
    <CompanyWalletContext.Provider value={{
      walletBalance,
      recentTransactions,
      walletStats,
      isLoading,
      isLoadingTransactions,
      currentPage,
      totalPages,
      totalTransactions,
      transactionsPerPage,
      addTransaction,
      withdrawFunds,
      fetchTransactions,
      setPage
    }}>
      {children}
    </CompanyWalletContext.Provider>
  );
};

export const useCompanyWallet = () => {
  const context = useContext(CompanyWalletContext);
  if (!context) throw new Error('useCompanyWallet must be used within CompanyWalletProvider');
  return context;
};